import { LoginForm } from "@/components/auth/login-form";
import { SignupForm } from "@/components/auth/signup-form";
import { AuthCards } from "@/components/auth/auth-cards";
import { Link } from "react-router";
import { ForgotPasswordForm } from "./forgot-password-form";
import { ResetPasswordForm } from "./reset-password-form";
import dgptLogo from "@/assets/dgptlogo.png";

type AuthMode = "login" | "register" | "invited_signup" | "forgot" | "reset";

const TITLES: Record<AuthMode, string> = {
  login: "Welcome back",
  register: "Create account",
  invited_signup: "Accept Invitation",
  forgot: "Forgot Password",
  reset: "Reset Password",
};

const DESCRIPTIONS: Record<AuthMode, string> = {
  login: "Enter your email and password to continue",
  register: "Create your account to get started",
  invited_signup:
    "You've been invited to manage a company. Create your account to accept the invitation.",
  forgot: "Enter your email to receive reset instructions",
  reset: "Enter your new password",
};

export const AuthComponent = ({
  mode,
  invitationCode,
  companyName,
}: {
  mode: AuthMode;
  invitationCode?: string | null;
  companyName?: string;
}) => {
  const description =
    mode === "invited_signup" && companyName
      ? `You've been invited to manage ${companyName}. Create your account to accept the invitation.`
      : DESCRIPTIONS[mode];

  const renderForm = () => {
    switch (mode) {
      case "login":
        return <LoginForm />;
      case "register":
        return <SignupForm />;
      case "invited_signup":
        return <SignupForm invitationCode={invitationCode} />;
      case "forgot":
        return <ForgotPasswordForm />;
      case "reset":
        return <ResetPasswordForm />;
    }
  };

  const renderBottomLink = () => {
    switch (mode) {
      case "login":
        return (
          <>
            <div className="flex justify-center gap-2">
              <Link
                to="/forgot-password"
                className="text-primary hover:underline"
              >
                Forgot password?
              </Link>
            </div>
            <div className="mt-2">
              Don't have an account?{" "}
              <Link to="https://startupfuel.zohobookings.com/#/dgpt" className="text-primary hover:underline">
                Book a demo
              </Link>
            </div>
          </>
        );
      case "register":
        return (
          <>
            Already have an account?{" "}
            <Link to="/sign-in" className="text-primary hover:underline">
              Sign in
            </Link>
          </>
        );
      case "forgot":
        return (
          <>
            Remember your password?{" "}
            <Link to="/sign-in" className="text-primary hover:underline">
              Sign in
            </Link>
          </>
        );
      case "reset":
        return (
          <Link to="/sign-in" className="text-primary hover:underline">
            Back to sign in
          </Link>
        );
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Auth form */}
      <div className="w-full lg:w-1/2 p-6 md:p-12 flex flex-col">
        <div className="flex-1 flex flex-col justify-center items-center">
          <div className="mb-8 text-center flex flex-col items-center">
            <img src={dgptLogo} alt="DGPT Logo" className="h-12 mb-4" />
            <h1 className="text-2xl font-bold mb-2">{TITLES[mode]}</h1>
            <p className="text-muted-foreground">{description}</p>
          </div>
          <div className="w-full max-w-[384px] px-4 md:px-0">
            {renderForm()}
            <div className="text-center mt-4 text-sm text-muted-foreground">
              {renderBottomLink()}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col md:flex-row justify-between text-sm text-muted-foreground gap-4 md:gap-0">
          <div>© 2025 All rights reserved</div>
          <div className="space-x-4">
            <a href="#" className="hover:text-primary">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-primary">
              Terms & Conditions
            </a>
          </div>
        </div>
      </div>

      {/* Right side - Carousel - Only visible on large screens */}
      <div className="hidden lg:block lg:w-1/2 p-2">
        <AuthCards />
      </div>
    </div>
  );
};
